import { improvisationCost } from "../constants.js";
/**
 * Quem joga em cada vaga, quando é o jogo que decide.
 *
 * Cada vaga vale a força do jogador ali menos o custo de improvisar
 * (`improvisationCost`), e a distribuição é a que soma mais no time inteiro, e
 * não vaga a vaga. Vaga a vaga, a zaga era preenchida primeiro com o melhor
 * número que houvesse, e esse número às vezes era o do centroavante; as vagas
 * do ataque ficavam com o que sobrou.
 *
 * Devolve um candidato por vaga, na ordem das vagas, ou nulo quando faltou
 * gente. Cada candidato entra no máximo uma vez.
 */
export function assignSlots(slots, candidates, natural, strength) {
    if (slots.length === 0)
        return [];
    // Faltando gente, vagas sobram: candidatos fantasmas, caros demais, as cobrem.
    const colunas = Math.max(candidates.length, slots.length);
    const FANTASMA = 1e6;
    const custo = slots.map((slot) => Array.from({ length: colunas }, (_, j) => {
        const candidato = candidates[j];
        if (candidato === undefined)
            return FANTASMA;
        return improvisationCost(natural(candidato), slot) - strength(candidato, slot);
    }));
    const escolha = menorCusto(custo);
    return escolha.map((j) => (j < candidates.length ? candidates[j] : null));
}
/**
 * O problema da designação (algoritmo húngaro), com mais colunas que linhas.
 *
 * Devolve, para cada linha, a coluna escolhida, de modo que a soma dos custos
 * seja a menor possível. Determinístico: empates ficam com a coluna que vem
 * antes.
 */
function menorCusto(custo) {
    const n = custo.length;
    const m = custo[0]?.length ?? 0;
    const u = new Array(n + 1).fill(0);
    const v = new Array(m + 1).fill(0);
    const dono = new Array(m + 1).fill(0);
    const caminho = new Array(m + 1).fill(0);
    for (let i = 1; i <= n; i += 1) {
        dono[0] = i;
        let j0 = 0;
        const menor = new Array(m + 1).fill(Infinity);
        const usada = new Array(m + 1).fill(false);
        do {
            usada[j0] = true;
            const i0 = dono[j0];
            let delta = Infinity;
            let j1 = 0;
            for (let j = 1; j <= m; j += 1) {
                if (usada[j])
                    continue;
                const atual = custo[i0 - 1][j - 1] - u[i0] - v[j];
                if (atual < menor[j]) {
                    menor[j] = atual;
                    caminho[j] = j0;
                }
                if (menor[j] < delta) {
                    delta = menor[j];
                    j1 = j;
                }
            }
            for (let j = 0; j <= m; j += 1) {
                if (usada[j]) {
                    u[dono[j]] = u[dono[j]] + delta;
                    v[j] = v[j] - delta;
                }
                else {
                    menor[j] = menor[j] - delta;
                }
            }
            j0 = j1;
        } while (dono[j0] !== 0);
        do {
            const j1 = caminho[j0];
            dono[j0] = dono[j1];
            j0 = j1;
        } while (j0 !== 0);
    }
    const resposta = new Array(n).fill(-1);
    for (let j = 1; j <= m; j += 1) {
        const linha = dono[j];
        if (linha > 0)
            resposta[linha - 1] = j - 1;
    }
    return resposta;
}
//# sourceMappingURL=assignment.js.map